import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface MasterItem {
  id: string;
  name: string;
  order: number;
}

interface MasterListProps {
  items: MasterItem[];
  title: string;
  description: string;
  emptyMessage: string;
  onAdd: (name: string) => void;
  onEdit: (id: string, name: string) => void;
  onDelete: (id: string) => void;
  onReorder: (id: string, direction: 'up' | 'down') => void;
}

export default function MasterList({
  items,
  title,
  description,
  emptyMessage,
  onAdd,
  onEdit,
  onDelete,
  onReorder,
}: MasterListProps) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const sorted = [...items].sort((a, b) => a.order - b.order);

  const handleStartAdd = () => {
    setAdding(true);
    setNewName('');
  };

  const handleConfirmAdd = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setNewName('');
    setAdding(false);
  };

  const handleCancelAdd = () => {
    setNewName('');
    setAdding(false);
  };

  const handleKeyDownAdd = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleConfirmAdd();
    if (e.key === 'Escape') handleCancelAdd();
  };

  const handleStartEdit = (item: MasterItem) => {
    setEditingId(item.id);
    setEditName(item.name);
    setDeleteConfirmId(null);
  };

  const handleConfirmEdit = () => {
    const trimmed = editName.trim();
    if (!trimmed || !editingId) return;
    onEdit(editingId, trimmed);
    setEditingId(null);
    setEditName('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const handleKeyDownEdit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleConfirmEdit();
    if (e.key === 'Escape') handleCancelEdit();
  };

  const handleDelete = (id: string) => {
    onDelete(id);
    setDeleteConfirmId(null);
    if (editingId === id) {
      setEditingId(null);
      setEditName('');
    }
  };

  return (
    <div className="bg-background-50 rounded-xl border border-background-200/70">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-background-200/70">
        <div>
          <h3 className="font-heading font-700 text-foreground-900 text-base">{title}</h3>
          <p className="text-foreground-500 text-xs font-label mt-0.5">{description}</p>
        </div>
        <button
          onClick={handleStartAdd}
          disabled={adding}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-primary-500 text-background-50 text-sm font-label font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="ri-add-line text-base" />
          {t('settings.addButton', { type: title.replace(' Master', '') })}
        </button>
      </div>

      {/* Add inline row */}
      {adding && (
        <div className="flex items-center gap-2 px-5 py-3 bg-primary-50/50 border-b border-primary-100">
          <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
            <i className="ri-add-line" />
          </div>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={handleKeyDownAdd}
            placeholder={t('settings.enterName')}
            className="flex-1 h-9 px-3 text-sm font-label bg-background-50 border border-background-300 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 outline-none transition-colors"
            autoFocus
          />
          <button
            onClick={handleConfirmAdd}
            disabled={!newName.trim()}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
          >
            <i className="ri-check-line text-sm" />
          </button>
          <button
            onClick={handleCancelAdd}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      )}

      {/* List */}
      <div className="divide-y divide-background-200/70">
        {sorted.length === 0 && !adding ? (
          <div className="flex flex-col items-center justify-center py-12 px-5 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-background-100 mb-3">
              <i className="ri-file-list-3-line text-2xl text-foreground-300" />
            </div>
            <p className="text-foreground-400 text-sm font-label">{emptyMessage}</p>
          </div>
        ) : (
          sorted.map((item, index) => (
            <div
              key={item.id}
              className={`flex items-center gap-2.5 px-5 py-3 transition-colors ${
                editingId === item.id ? 'bg-primary-50/40' : 'hover:bg-background-100/70'
              }`}
            >
              {/* Reorder buttons */}
              <div className="flex flex-col gap-0.5 flex-shrink-0">
                <button
                  onClick={() => onReorder(item.id, 'up')}
                  disabled={index === 0}
                  className="w-6 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-25 disabled:cursor-not-allowed"
                  title={t('settings.moveUp')}
                >
                  <i className="ri-arrow-up-s-line text-xs" />
                </button>
                <button
                  onClick={() => onReorder(item.id, 'down')}
                  disabled={index === sorted.length - 1}
                  className="w-6 h-5 flex items-center justify-center rounded text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer disabled:opacity-25 disabled:cursor-not-allowed"
                  title={t('settings.moveDown')}
                >
                  <i className="ri-arrow-down-s-line text-xs" />
                </button>
              </div>

              {/* Order number */}
              <span className="w-6 text-center text-xs font-label font-medium text-foreground-400 flex-shrink-0">
                {index + 1}
              </span>

              {/* Content: edit mode vs display */}
              {editingId === item.id ? (
                <>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={handleKeyDownEdit}
                    className="flex-1 h-9 px-3 text-sm font-label bg-background-50 border border-primary-400 rounded-lg focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 outline-none transition-colors"
                    autoFocus
                  />
                  <button
                    onClick={handleConfirmEdit}
                    disabled={!editName.trim()}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    <i className="ri-check-line text-sm" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm font-label font-medium text-foreground-800 truncate">
                    {item.name}
                  </span>

                  {/* Edit button */}
                  <button
                    onClick={() => handleStartEdit(item)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer flex-shrink-0"
                    title="Edit"
                  >
                    <i className="ri-pencil-line text-sm" />
                  </button>

                  {/* Delete button */}
                  {deleteConfirmId === item.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="px-2.5 py-1.5 text-xs font-label font-medium text-semantic-error bg-semantic-error-bg rounded-md hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {t('settings.deleteConfirm')}
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(null)}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-xs" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirmId(item.id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-semantic-error hover:bg-red-50 transition-colors cursor-pointer flex-shrink-0"
                      title="Delete"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Footer stats */}
      {sorted.length > 0 && (
        <div className="px-5 py-3 border-t border-background-200/70">
          <p className="text-foreground-400 text-xs font-label">
            {sorted.length} item{sorted.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
}